import { Alert, Button, EmptyState, ErrorState, Skeleton } from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { Download, Users } from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/errors'
import { useCurrentPermissions, useSegmentMembers, useSegments } from '@/api/queries'
import { CampaignsSection } from '@/features/crm/campaigns-section'
import type { SegmentKey, SegmentMember } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { notify } from '@/lib/toast'

/**
 * I segmenti dell'anagrafica — F6, CRM-04.
 *
 * ## I numeri e i nomi sono due permessi
 *
 * Il catalogo con i conteggi è `gym_member:read`, l'elenco delle persone
 * `gym_member:export`: un segmento aperto è un'estrazione in blocco della rubrica con
 * i recapiti dentro, cioè la cosa che quel permesso governa. Chi ha solo il primo vede
 * i numeri e una frase che spiega perché non vede i nomi — non un elenco vuoto, che
 * direbbe «non c'è nessuno».
 *
 * ## Ogni riga porta il motivo per cui è lì
 *
 * La scadenza per gli scaduti, l'ultimo accesso per gli inattivi, il compleanno per i
 * compleanni. Un elenco di nomi senza quel dato obbliga chi telefona ad aprire
 * duecento schede — e chi telefona ha una lista in mano, non un browser.
 *
 * ## Le campagne stanno qui e non in una pagina propria
 *
 * «A chi scrivo» è la domanda che si fa prima di «cosa scrivo», e la risposta è già su
 * questa pagina con il suo numero accanto (CRM-03 §invio a un segmento). Una pagina
 * separata avrebbe costretto a scegliere il segmento due volte — una per guardarlo e una
 * per scrivergli — e a ricordarsi il conteggio nel passaggio.
 *
 * ## La copia dei recapiti è un gesto esplicito
 *
 * Il pulsante copia email e telefoni negli appunti, e non c'è un'esportazione in CSV:
 * quella esiste già per l'anagrafica intera (ANA-08) e passa dal registro. Qui il
 * gesto serve a incollare in un client di posta, e la traccia l'ha già lasciata
 * l'apertura del segmento.
 */
export const Route = createFileRoute('/_app/segmenti/')({
  component: SegmentsPage,
})

function SegmentsPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('gym_member:read')
  const canSeeNames = permissions.has('gym_member:export')
  /**
   * Mandare a un segmento chiede **due** permessi, come la conversione di un contatto:
   * `lead:write` per mandare e `gym_member:read` per agire sull'anagrafica come insieme.
   * Non `gym_member:export`: chi manda vede un numero, non dei nomi.
   */
  const canSend = permissions.has('lead:write')
  const segments = useSegments()
  const [opened, setOpened] = useState<SegmentKey | null>(null)

  if (!canRead) {
    return (
      <>
        <PageHeader title="Segmenti" description="Chi chiamare, e perché." />
        <Alert variant="neutral" title="Serve «gym_member:read»" icon={false}>
          Il tuo ruolo non legge l’anagrafica.
        </Alert>
      </>
    )
  }

  if (segments.isError && !segments.data) {
    return (
      <ErrorState message={errorMessage(segments.error)} onRetry={() => void segments.refetch()} />
    )
  }

  const rows = segments.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Segmenti"
        description="Quattro domande che una palestra si fa ogni settimana. I numeri li vede chi legge l’anagrafica, i nomi chi può estrarla."
      />

      {!canSeeNames ? (
        <Alert variant="neutral" className="mb-4" title="Vedi i numeri, non i nomi" icon={false}>
          Aprire un segmento è un’estrazione in blocco della rubrica con i recapiti dentro, e
          richiede «gym_member:export». Un conteggio invece non identifica nessuno.
        </Alert>
      ) : null}

      {segments.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {rows.map((segment) => (
            <li key={segment.key} className="border-border bg-card rounded-lg border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{segment.label}</p>
                  <p className="text-muted-foreground mt-1 text-xs">{segment.description}</p>
                </div>
                <p className="typo-display-sm shrink-0 tabular-nums">{segment.count}</p>
              </div>
              {canSeeNames && segment.count > 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => setOpened(segment.key)}
                >
                  <Users className="size-4" />
                  Apri l’elenco
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {segments.isPending ? null : <CampaignsSection segments={rows} canSend={canSend} />}

      {opened !== null ? (
        <SegmentMembers
          segmentKey={opened}
          label={rows.find((row) => row.key === opened)?.label ?? ''}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </>
  )
}

function SegmentMembers({
  segmentKey,
  label,
  onClose,
}: {
  segmentKey: SegmentKey
  label: string
  onClose: () => void
}) {
  const list = useSegmentMembers(segmentKey)
  const rows = list.data?.data ?? []

  /** I recapiti negli appunti: serve a incollarli in un client di posta. */
  async function copyContacts(): Promise<void> {
    const contacts = rows
      .map((row) => row.email ?? row.phone)
      .filter((value): value is string => value !== null && value !== undefined)
    if (contacts.length === 0) {
      notify({ message: 'Nessun recapito da copiare.', variant: 'danger' })
      return
    }
    try {
      await navigator.clipboard.writeText(contacts.join(', '))
      notify({
        message:
          contacts.length === rows.length
            ? `${String(contacts.length)} recapiti copiati.`
            : // Il numero diverso va detto: chi incolla crede di aver preso tutti.
              `${String(contacts.length)} recapiti copiati su ${String(rows.length)}: gli altri non ne hanno.`,
        variant: 'success',
      })
    } catch {
      notify({ message: 'Gli appunti non sono disponibili in questo browser.', variant: 'danger' })
    }
  }

  return (
    <section className="border-border bg-card mt-4 rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{label}</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void copyContacts()}>
            <Download className="size-4" />
            Copia i recapiti
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
        </div>
      </div>

      {list.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : list.isError ? (
        <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState icon={<Users />} title="Nessuno" description="Il segmento è vuoto adesso." />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((row) => (
            <li key={row.gymMemberId} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {row.lastName} {row.firstName}
                </p>
                <p className="text-muted-foreground text-xs">
                  {[row.phone, row.email].filter(Boolean).join(' · ') || 'nessun recapito'}
                  {fact(row)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Il motivo per cui questa persona è nel segmento, che è il dato che serve al telefono. */
function fact(row: SegmentMember): string {
  if (row.expiredOn !== undefined && row.expiredOn !== null) return ` · scaduto il ${row.expiredOn}`
  if (row.lastAccessAt !== undefined) {
    return row.lastAccessAt === null
      ? ' · mai entrato'
      : ` · ultimo ingresso ${row.lastAccessAt.slice(0, 10)}`
  }
  if (row.bornOn !== undefined && row.bornOn !== null) {
    return ` · compie gli anni il ${row.bornOn.slice(8, 10)}`
  }
  return ''
}
